import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Steps,
  Switch,
  Tag,
  Typography
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useTranslation } from "react-i18next";
import { isAuthenticated } from "../../../commons/Auth/auth";
import { Success, Error } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import {
  CONTRACT_TYPES,
  MINIJOB_LIMIT,
  MIDIJOB_LIMIT,
  WERKSTUDENT_MAX_HOURS,
  buildTemplateVars,
  contractQuillFormats,
  contractQuillModules,
  describeType,
  fillTemplate,
  getContractType
} from "./contractTypes";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./Contracts.css";

const { Text, Paragraph } = Typography;
const authHeader = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

// Fields validated before leaving the details step.
const DETAIL_FIELDS = [
  "employee",
  "partyName",
  "title",
  "startDate",
  "endDate",
  ["terms", "salary"],
  ["terms", "weeklyHours"]
];

const defaultTerms = type => ({
  salaryType: type.employment ? "monthly" : "flat",
  weeklyHours: { vollzeit: 40, teilzeit: 20, minijob: 10, midijob: 20, werkstudent: 20 }[type.key],
  vacationDays: type.employment ? (type.key === "vollzeit" ? 25 : 20) : undefined,
  probationMonths: ["vollzeit", "teilzeit", "midijob", "werkstudent"].includes(type.key)
    ? 6
    : type.key === "praktikum"
      ? 1
      : undefined,
  noticePeriod: type.employment ? undefined : "4 Wochen zum Monatsende"
});

/** Initial form values for an existing contract. */
const valuesFromContract = contract => ({
  contractType: contract.contractType || "sonstiges",
  title: contract.title,
  employee: contract.employee?._id || contract.employee || undefined,
  partyName: contract.partyName,
  partyCompany: contract.partyCompany,
  partyTaxNumber: contract.partyTaxNumber,
  address: contract.address,
  city: contract.city,
  province: contract.province,
  postalCode: contract.postalCode,
  email: contract.email,
  phone: contract.phone,
  terms: contract.terms || {},
  fixedTerm: Boolean(contract.fixedTerm || contract.endDate),
  startDate: contract.startDate ? dayjs(contract.startDate) : undefined,
  endDate: contract.endDate ? dayjs(contract.endDate) : undefined
});

/**
 * Creates or edits a contract in three steps: pick the type, fill in the parties and terms,
 * then edit the contract text generated from the type's template.
 *
 * With `contract` it edits; the contract must have been loaded with its text (contractDetails).
 */
const ContractFormModal = ({ open, onClose, onSaved, contract, presetEmployee }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const editing = Boolean(contract);
  const [step, setStep] = useState(editing ? 1 : 0);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [text, setText] = useState(contract?.contractDetails || "");
  // Whether the user changed the generated text by hand. While they have not, the text
  // follows the details step; once they have, it is only rebuilt when they ask for it.
  const [textEdited, setTextEdited] = useState(Boolean(contract?.contractDetails));
  const [saving, setSaving] = useState(false);
  const quillRef = useRef(null);
  const isMobile = useIsMobile();

  const contractType = Form.useWatch("contractType", form);
  const fixedTerm = Form.useWatch("fixedTerm", form);
  const employeeId = Form.useWatch("employee", form);
  const salary = Form.useWatch(["terms", "salary"], form);
  const salaryType = Form.useWatch(["terms", "salaryType"], form);
  const weeklyHours = Form.useWatch(["terms", "weeklyHours"], form);
  const type = getContractType(contractType);
  const isFixedTerm = Boolean(type.alwaysFixedTerm || fixedTerm);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [templateRes, employeeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/contracts/templates`, {
            headers: authHeader()
          }),
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${isAuthenticated()?.serviceProviderId}`,
            { headers: authHeader() }
          )
        ]);
        setTemplates(templateRes.data.templates);
        setEmployer(templateRes.data.employer);
        setEmployees(Array.isArray(employeeRes.data) ? employeeRes.data : []);
      } catch (err) {
        generalErrorAlert(err);
      }
    };
    load();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setStep(editing ? 1 : 0);
    setText(contract?.contractDetails || "");
    setTextEdited(Boolean(contract?.contractDetails));
    form.resetFields();
    if (editing) {
      form.setFieldsValue(valuesFromContract(contract));
    } else if (presetEmployee) {
      form.setFieldsValue({ employee: presetEmployee });
    }
  }, [open, contract]);

  const selectedEmployee = useMemo(
    () => employees.find(employee => employee._id === employeeId),
    [employees, employeeId]
  );

  const chooseType = key => {
    const chosen = getContractType(key);
    const current = form.getFieldsValue(true);
    form.setFieldsValue({
      contractType: key,
      title: templates?.[key]?.title,
      fixedTerm: chosen.alwaysFixedTerm ? true : current.fixedTerm,
      terms: { ...defaultTerms(chosen), ...(editing ? current.terms : {}) }
    });
    // A new type means a different template.
    setTextEdited(false);
    setText("");
    setStep(1);
  };

  // Copies the employee's address and contact details into the contract, and takes over
  // what the employee record already knows about the job, where the form is still empty.
  const onEmployeeChange = id => {
    const employee = employees.find(e => e._id === id);
    if (!employee) return;
    const current = form.getFieldsValue(true);
    const street = [employee.street, employee.streetNumber].filter(Boolean).join(" ");
    const terms = current.terms || {};
    form.setFieldsValue({
      address: street || current.address,
      city: employee.city || current.city,
      postalCode: employee.zipCode || current.postalCode,
      province: employee.state || current.province,
      email: employee.contractEmail || employee.email || current.email,
      phone: employee.contractPhone || employee.phone || current.phone,
      terms: {
        ...terms,
        jobTitle: terms.jobTitle || employee.designation?.title,
        // Salary and vacation on the employee record are real data and beat the type's
        // defaults. Weekly hours do not: payroll.regularWeeklyHours defaults to 40 for
        // everyone, which would overwrite a Minijob's 10.
        salary: employee.salary ?? terms.salary,
        salaryType: employee.salary
          ? employee.workSchedule === "hourly"
            ? "hourly"
            : "monthly"
          : terms.salaryType,
        weeklyHours: terms.weeklyHours ?? employee.payroll?.regularWeeklyHours,
        vacationDays: employee.vacationsYear ?? terms.vacationDays
      }
    });
  };

  const generateText = () => {
    const values = form.getFieldsValue(true);
    const template = templates?.[values.contractType]?.body || "";
    setText(fillTemplate(template, buildTemplateVars(values, employer, selectedEmployee)));
  };

  const next = async () => {
    try {
      await form.validateFields(DETAIL_FIELDS);
    } catch {
      return;
    }
    if (!textEdited) generateText();
    setStep(2);
  };

  const save = async (resetSignatures = false) => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      setStep(1);
      return;
    }
    const all = { ...form.getFieldsValue(true), ...values };
    if (!text.replace(/<[^>]*>/g, "").trim()) {
      Error(t("The contract text is empty."));
      return;
    }
    const chosen = getContractType(all.contractType);
    const payload = {
      ...all,
      employee: all.employee || null,
      // For employees the party is the employee; keeping the name on the contract makes
      // it searchable and keeps it readable if the employee record is deleted.
      partyName: chosen.employment
        ? [selectedEmployee?.firstName, selectedEmployee?.lastName].filter(Boolean).join(" ") ||
          all.partyName
        : all.partyName,
      fixedTerm: Boolean(chosen.alwaysFixedTerm || all.fixedTerm),
      startDate: all.startDate ? all.startDate.format("YYYY-MM-DD") : null,
      endDate:
        (chosen.alwaysFixedTerm || all.fixedTerm) && all.endDate
          ? all.endDate.format("YYYY-MM-DD")
          : null,
      contractDetails: text,
      resetSignatures
    };
    setSaving(true);
    try {
      const res = editing
        ? await axios.put(
            `${process.env.REACT_APP_BACKEND_URL}/contracts/update/${contract._id}`,
            payload,
            { headers: authHeader() }
          )
        : await axios.post(`${process.env.REACT_APP_BACKEND_URL}/contracts/add`, payload, {
            headers: authHeader()
          });
      Success(t(res.data.successMessage));
      onSaved?.(res.data.contract);
      onClose();
    } catch (err) {
      if (err?.response?.data?.code === "SIGNATURES_WOULD_BE_RESET") {
        Modal.confirm({
          title: t("This contract has already been signed by one party"),
          content: t(
            "Saving your changes removes that signature, and the contract has to be signed again."
          ),
          okText: t("Save and remove signature"),
          cancelText: t("Cancel"),
          onOk: () => save(true)
        });
      } else if (err?.response?.data?.code === "CONTRACT_SIGNED") {
        Error(
          t(
            "This contract has been signed by both parties and can no longer be changed. Create an amendment instead."
          )
        );
      } else {
        generalErrorAlert(err);
      }
    } finally {
      setSaving(false);
    }
  };

  const typeStep = (
    <>
      <Paragraph type="secondary">
        {t("Choose the kind of contract. You can edit the generated text in the last step.")}
      </Paragraph>
      <Row gutter={[12, 12]} align="stretch">
        {CONTRACT_TYPES.map(item => (
          <Col xs={24} sm={12} md={8} key={item.key}>
            <Card
              hoverable
              size="small"
              className={`contract-type-card${contractType === item.key ? " is-selected" : ""}`}
              onClick={() => chooseType(item.key)}
            >
              <Flex justify="space-between" align="start" gap={8}>
                <Text strong>{t(item.label)}</Text>
                {templates?.[item.key]?.isCustom && <Tag color="blue">{t("Own template")}</Tag>}
              </Flex>
              <div className="contract-type-card__desc">{describeType(item, t)}</div>
            </Card>
          </Col>
        ))}
      </Row>
    </>
  );

  const monthlyAmount =
    salaryType === "hourly" && salary && weeklyHours
      ? (salary * weeklyHours * 13) / 3
      : salaryType === "monthly"
        ? salary
        : undefined;

  const detailsStep = (
    <>
      <Form.Item name="contractType" hidden>
        <Input />
      </Form.Item>
      <Flex align="center" gap={8} wrap="wrap" className="mb-3">
        <Tag color="geekblue">{t(type.label)}</Tag>
        {!editing && (
          <Button size="small" type="link" onClick={() => setStep(0)}>
            {t("Change type")}
          </Button>
        )}
        {editing && (
          <Select
            size="small"
            value={contractType}
            style={{ minWidth: 180 }}
            onChange={chooseType}
            options={CONTRACT_TYPES.map(item => ({ value: item.key, label: t(item.label) }))}
          />
        )}
      </Flex>
      {/* Form rows: Form.Item brings its own bottom margin, so only a horizontal gutter. */}
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <Form.Item
            label={t("Contract title")}
            name="title"
            rules={[{ required: true, message: t("enter", { field: t("Contract title") }) }]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Text strong>{type.employment ? t("Employee") : t("Contract partner")}</Text>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={type.employment ? t("Employee") : t("Linked employee (optional)")}
            name="employee"
            rules={[
              {
                required: type.employment,
                message: t("select", { field: t("Employee") })
              }
            ]}
          >
            <Select
              allowClear={!type.employment}
              showSearch
              optionFilterProp="label"
              placeholder={t("select", { field: t("Employee") })}
              onChange={onEmployeeChange}
              options={employees.map(employee => ({
                value: employee._id,
                label: `${employee.firstName} ${employee.lastName}`
              }))}
            />
          </Form.Item>
        </Col>
        {!type.employment && (
          <>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("Name of the contract partner")}
                name="partyName"
                rules={[{ required: true, message: t("enter", { field: t("Name") }) }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Company")} name="partyCompany">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Tax number / VAT ID")} name="partyTaxNumber">
                <Input />
              </Form.Item>
            </Col>
          </>
        )}
        <Col xs={24} md={12}>
          <Form.Item label={t("Street Address")} name="address">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={t("Postal Code")} name="postalCode">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={12} md={6}>
          <Form.Item label={t("City")} name="city">
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={t("Email")}
            name="email"
            rules={[{ type: "email", message: t("enter", { field: t("Email") }) }]}
          >
            <Input type="email" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t("Phone")} name="phone">
            <Input />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Text strong>{t("Terms")}</Text>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={type.employment ? t("Job title") : t("Subject of the contract")}
            name={["terms", "jobTitle"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label={type.employment ? t("Place of work") : t("Place of performance")}
            name={["terms", "workplace"]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label={t("Start Date")}
            name="startDate"
            rules={[{ required: true, message: t("select", { field: t("Start Date") }) }]}
          >
            <DatePicker className="w-100" format="DD.MM.YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t("Fixed-term")} name="fixedTerm" valuePropName="checked">
            <Switch disabled={type.alwaysFixedTerm} />
          </Form.Item>
        </Col>
        {isFixedTerm && (
          <Col xs={24} md={8}>
            <Form.Item
              label={t("End Date")}
              name="endDate"
              dependencies={["startDate"]}
              rules={[
                { required: true, message: t("select", { field: t("End Date") }) },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const start = getFieldValue("startDate");
                    if (!value || !start || value.isAfter(start)) return Promise.resolve();
                    return Promise.reject(
                      new window.Error(t("End date must be after the start date"))
                    );
                  }
                })
              ]}
            >
              <DatePicker className="w-100" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
        )}
        {isFixedTerm && type.employment && (
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={t("A fixed term must be agreed in writing")}
              description={t(
                "Under § 14 (4) TzBfG the fixed term of an employment contract is only valid with handwritten signatures on paper (or a qualified electronic signature). Print the contract and have it signed by hand, otherwise the employment counts as permanent."
              )}
            />
          </Col>
        )}
        {type.key === "aushilfe" && (
          <Col xs={24}>
            <Alert
              type="info"
              showIcon
              className="mb-3"
              message={t(
                "Short-term employment is limited to 3 months or 70 working days in a calendar year."
              )}
            />
          </Col>
        )}
        {type.key !== "sonstiges" && (
          <Col xs={24} md={8}>
            <Form.Item
              label={type.employment ? t("Hours per week") : t("Hours per week (optional)")}
              name={["terms", "weeklyHours"]}
              rules={[
                {
                  required: type.employment && type.key !== "praktikum",
                  message: t("enter", { field: t("Hours per week") })
                }
              ]}
            >
              <InputNumber min={0} max={60} className="w-100" />
            </Form.Item>
          </Col>
        )}
        <Col xs={24} md={8}>
          <Form.Item
            label={type.employment ? t("Gross pay (€)") : t("Fee (€, net)")}
            name={["terms", "salary"]}
          >
            <InputNumber min={0} step={0.01} className="w-100" decimalSeparator="," />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label={t("Paid")} name={["terms", "salaryType"]}>
            <Select
              options={[
                { value: "monthly", label: t("per month") },
                { value: "hourly", label: t("per hour") },
                { value: "flat", label: t("flat rate") }
              ]}
            />
          </Form.Item>
        </Col>
        {type.key === "minijob" && monthlyAmount > MINIJOB_LIMIT && (
          <Col xs={24}>
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t("Above the mini job limit of {{limit}} € a month", {
                limit: MINIJOB_LIMIT
              })}
              description={t("About {{amount}} € a month. Consider a midi job instead.", {
                amount: Math.round(monthlyAmount)
              })}
            />
          </Col>
        )}
        {type.key === "midijob" &&
          monthlyAmount &&
          (monthlyAmount <= MINIJOB_LIMIT || monthlyAmount > MIDIJOB_LIMIT) && (
            <Col xs={24}>
              <Alert
                type="warning"
                showIcon
                className="mb-3"
                message={t("Outside the midi job range of {{min}} € to {{max}} € a month", {
                  min: MINIJOB_LIMIT,
                  max: MIDIJOB_LIMIT
                })}
              />
            </Col>
          )}
        {type.key === "werkstudent" && weeklyHours > WERKSTUDENT_MAX_HOURS && (
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={t(
                "More than 20 hours a week during the semester ends the working student status."
              )}
            />
          </Col>
        )}
        {type.employment && (
          <>
            <Col xs={24} md={8}>
              <Form.Item label={t("Vacation days per year")} name={["terms", "vacationDays"]}>
                <InputNumber min={0} max={60} className="w-100" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("Probation (months)")} name={["terms", "probationMonths"]}>
                <InputNumber min={0} max={6} className="w-100" />
              </Form.Item>
            </Col>
          </>
        )}
        <Col xs={24} md={8}>
          <Form.Item label={t("Notice period")} name={["terms", "noticePeriod"]}>
            <Input placeholder={type.employment ? t("Statutory (§ 622 BGB)") : ""} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const textStep = (
    <>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} className="mb-2">
        <Text type="secondary">
          {t(
            "This text is generated from the template. Edit it freely: what you see here is what gets signed."
          )}
        </Text>
        {textEdited ? (
          <Popconfirm
            title={t("Rebuild the text from the template?")}
            description={t("Your changes to the text will be lost.")}
            okText={t("Rebuild")}
            cancelText={t("Cancel")}
            onConfirm={() => {
              generateText();
              setTextEdited(false);
            }}
          >
            <Button size="small">{t("Rebuild from template")}</Button>
          </Popconfirm>
        ) : null}
      </Flex>
      {text.includes("__________") && (
        <Alert
          type="info"
          showIcon
          className="mb-2"
          message={t("Blank lines (__________) mark details that are still missing.")}
        />
      )}
      <div className="contract-editor">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          modules={contractQuillModules}
          formats={contractQuillFormats}
          value={text}
          onChange={(value, _delta, source) => {
            setText(value);
            if (source === "user") setTextEdited(true);
          }}
        />
      </div>
      <Paragraph type="secondary" className="mt-2 mb-0 contract-legal-note">
        {t(
          "Templates are a starting point based on German law and do not replace legal advice. Check collective agreements (Tarifverträge) that apply to you."
        )}
      </Paragraph>
    </>
  );

  const footer = (
    <Flex justify="space-between" gap={8} className="contract-form-modal__footer">
      <Button onClick={onClose}>{t("Cancel")}</Button>
      <Space wrap>
        {step > (editing ? 1 : 0) && <Button onClick={() => setStep(step - 1)}>{t("Back")}</Button>}
        {step === 1 && (
          <Button type="primary" onClick={next}>
            {t("Next")}
          </Button>
        )}
        {step === 2 && (
          <Button type="primary" loading={saving} onClick={() => save(false)}>
            {editing ? t("Save changes") : t("Create contract")}
          </Button>
        )}
      </Space>
    </Flex>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editing ? t("Update Contract") : t("Add Contract")}
      width={960}
      centered
      destroyOnClose
      maskClosable={false}
      footer={footer}
      className="contract-form-modal"
    >
      <Steps
        size="small"
        current={step}
        // Vertical steps would push the form a screen down on phones.
        responsive={false}
        labelPlacement={isMobile ? "vertical" : "horizontal"}
        className="mb-4 contract-form-modal__steps"
        items={[
          { title: t("Contract type") },
          { title: t("Parties and terms") },
          { title: t("Contract text") }
        ]}
      />
      <Form form={form} layout="vertical" preserve>
        <div style={{ display: step === 0 ? "block" : "none" }}>{typeStep}</div>
        <div style={{ display: step === 1 ? "block" : "none" }}>{detailsStep}</div>
      </Form>
      {step === 2 && textStep}
    </Modal>
  );
};

export default ContractFormModal;
